import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select, Textarea, Toggle } from '@/components/ui/Input';
import { ErrorState, PageLoader } from '@/components/ui/States';
import { useToast } from '@/components/ui/Toast';

/**
 * Penyunting konten halaman depan untuk admin.
 *
 * Perubahan tersimpan ke basis data dan langsung dibaca halaman publik lewat
 * /public/site-content - tanpa build ulang, tanpa deploy. Bagian yang tidak
 * pernah disunting tetap memakai teks bawaan dari kode.
 */
const NADA_PRATINJAU = {
  promo: 'bg-[#c8f751] text-[#0b0b14]',
  info: 'bg-sky-500 text-white',
  peringatan: 'bg-amber-400 text-[#0b0b14]',
  gelap: 'bg-white/10 text-white',
};

const LEBAR_PRATINJAU = { sempit: 'max-w-sm', sedang: 'max-w-xl', penuh: 'max-w-full' };

/**
 * Pratinjau banner pengumuman.
 *
 * Ditampilkan di atas latar gelap seperti halaman depan yang sebenarnya.
 * Pratinjau di atas latar terang akan menyesatkan justru untuk pilihan warna
 * Gelap, yang memang dirancang menyatu dengan latarnya.
 */
const PratinjauPengumuman = ({ isi }) => {
  const adaTombol = Boolean(isi.tautanUrl && isi.tautanLabel);

  return (
    <div>
      <p className="mb-2 text-xs font-semibold text-[var(--text-secondary)]">
        Beginilah tampilannya di halaman depan
      </p>
      <div className="overflow-hidden rounded-lg bg-[#0b0b14] p-3">
        <div
          className={cn(
            'relative rounded px-4 py-2.5',
            NADA_PRATINJAU[isi.nada] ?? NADA_PRATINJAU.promo,
          )}
        >
          <div
            className={cn(
              'mx-auto flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-center',
              LEBAR_PRATINJAU[isi.lebar] ?? LEBAR_PRATINJAU.sedang,
            )}
          >
            {isi.gambarUrl && (
              <img
                src={isi.gambarUrl}
                alt=""
                className="h-6 w-auto shrink-0 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <p className="text-sm leading-snug">
              {isi.judul && <span className="font-bold">{isi.judul}</span>}
              {isi.judul && isi.text && <span className="mx-1.5 opacity-50">|</span>}
              {isi.text && <span className="font-medium">{isi.text}</span>}
              {!isi.judul && !isi.text && (
                <span className="opacity-50">Isi judul atau teksnya untuk melihat hasilnya</span>
              )}
            </p>
            {adaTombol && (
              <span className="shrink-0 rounded-full bg-black/15 px-3 py-1 text-xs font-bold">
                {isi.tautanLabel}
              </span>
            )}
          </div>
          {isi.bisaDitutup && (
            <span className="absolute top-1.5 right-3 text-xs opacity-60">&times;</span>
          )}
        </div>

        {/* Menu ikut ditampilkan supaya terlihat bahwa ia berada DI BAWAH
            banner, bukan menimpanya. Itu persis kekeliruan yang diperbaiki. */}
        <div className="mt-1 flex items-center justify-between rounded bg-white/5 px-4 py-2.5">
          <span className="text-xs font-bold text-white">Crawel</span>
          <span className="text-[10px] text-white/40">menu halaman depan</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Daftar periksa kelengkapan kontak.
 *
 * ── Kenapa ini ada ──────────────────────────────────────────────────────
 *
 * Penyedia pembayaran memeriksa halaman utama sebelum menyetujui pengajuan,
 * dan yang mereka cari selalu tiga hal yang sama: email, nomor telepon, dan
 * alamat usaha. Ruas yang dikosongkan di layar ini tidak ditampilkan di
 * halaman depan, jadi satu ruas terlewat berarti pengajuan ditolak dan baru
 * ketahuan berminggu-minggu kemudian lewat email penolakan.
 *
 * WhatsApp sengaja tidak dihitung menggantikan telepon. Nomornya memang nomor
 * telepon, tetapi labelnya di halaman depan tertulis "WhatsApp", dan peninjau
 * yang mencari "nomor telepon" tidak selalu menerimanya.
 *
 * Yang sudah terisi ikut ditampilkan, bukan hanya yang kurang. Daftar yang
 * cuma memarahi tidak memberi tahu seberapa dekat pekerjaannya dari selesai.
 */
const WAJIB_KONTAK = [
  ['email', 'Email support'],
  ['telepon', 'Nomor telepon'],
  ['alamat', 'Alamat usaha'],
];

const KelengkapanKontak = ({ kontak }) => {
  const kurang = WAJIB_KONTAK.filter(([kunci]) => !String(kontak?.[kunci] ?? '').trim());

  if (kurang.length === 0) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-success-200 bg-success-50 px-4 py-3 dark:border-success-500/25 dark:bg-success-500/10">
        <Check
          className="mt-0.5 size-[18px] shrink-0 text-success-600 dark:text-success-500"
          aria-hidden="true"
        />
        <p className="text-sm font-medium text-success-700 dark:text-success-500">
          Email, nomor telepon, dan alamat usaha sudah lengkap. Ini tiga hal yang diperiksa
          penyedia pembayaran pada halaman utama.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-warning-200 bg-warning-50 px-4 py-3 dark:border-warning-500/25 dark:bg-warning-500/10">
      <div className="flex items-start gap-3">
        <AlertTriangle
          className="mt-0.5 size-[18px] shrink-0 text-warning-600 dark:text-warning-500"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-warning-700 dark:text-warning-500">
            {kurang.length === 1 ? 'Satu hal belum diisi' : `${kurang.length} hal belum diisi`}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-warning-700/90 dark:text-warning-500/90">
            Penyedia pembayaran memeriksa email, nomor telepon, dan alamat usaha di halaman
            utama sebelum menyetujui pengajuan. Ruas yang kosong tidak ditampilkan di sana.
          </p>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {kurang.map(([kunci, label]) => (
              <li
                key={kunci}
                className="rounded-full bg-warning-100 px-2.5 py-0.5 text-xs font-bold text-warning-700 dark:bg-warning-500/20 dark:text-warning-500"
              >
                {label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export const SiteContentPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['site-content'],
    queryFn: () => api.get('/platform/site-content'),
  });

  const [form, setForm] = useState(null);

  // Formulir diisi sekali dari server; sesudah itu state lokal yang memimpin
  // supaya ketikan tidak tertimpa oleh refetch di latar belakang.
  useEffect(() => {
    if (data?.data && form === null) {
      const d = data.data;
      setForm({
        hero: { badge: d.hero?.badge ?? '', title: d.hero?.title ?? '', subtitle: d.hero?.subtitle ?? '' },
        stats: d.stats?.length ? d.stats : [{ value: '', label: '' }],
        announcement: {
          enabled: d.announcement?.enabled ?? false,
          judul: d.announcement?.judul ?? '',
          text: d.announcement?.text ?? '',
          gambarUrl: d.announcement?.gambarUrl ?? '',
          tautanUrl: d.announcement?.tautanUrl ?? '',
          tautanLabel: d.announcement?.tautanLabel ?? '',
          nada: d.announcement?.nada ?? 'promo',
          lebar: d.announcement?.lebar ?? 'sedang',
          bisaDitutup: d.announcement?.bisaDitutup ?? true,
        },
        faq: d.faq ?? [],
        contactEmail: d.contactEmail ?? '',
        tentang: {
          aktif: d.tentang?.aktif ?? true,
          judul: d.tentang?.judul ?? '',
          isi: d.tentang?.isi ?? '',
          poin: d.tentang?.poin ?? [],
        },
        kontak: {
          aktif: d.kontak?.aktif ?? true,
          judul: d.kontak?.judul ?? '',
          pengantar: d.kontak?.pengantar ?? '',
          email: d.kontak?.email ?? '',
          telepon: d.kontak?.telepon ?? '',
          whatsapp: d.kontak?.whatsapp ?? '',
          alamat: d.kontak?.alamat ?? '',
          namaUsaha: d.kontak?.namaUsaha ?? '',
          jamOperasional: d.kontak?.jamOperasional ?? '',
        },
        sosial: {
          instagram: d.sosial?.instagram ?? '',
          linkedin: d.sosial?.linkedin ?? '',
          twitter: d.sosial?.twitter ?? '',
          youtube: d.sosial?.youtube ?? '',
          github: d.sosial?.github ?? '',
        },
      });
    }
  }, [data, form]);

  const simpan = useMutation({
    mutationFn: () =>
      api.put('/platform/site-content', {
        ...form,
        stats: form.stats.filter((s) => s.value.trim() && s.label.trim()),
        faq: form.faq.filter((f) => f.question.trim() && f.answer.trim()),
        // Poin tanpa judul tidak punya arti di layar, dan validator menolaknya.
        tentang: {
          ...form.tentang,
          poin: (form.tentang.poin ?? []).filter((p) => p.judul.trim()),
        },
      }),
    onSuccess: () => {
      toast.success('Konten tersimpan', { description: 'Halaman depan sudah memakai teks baru.' });
      queryClient.invalidateQueries({ queryKey: ['site-content'] });
    },
    onError: (err) =>
      toast.error('Gagal menyimpan', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  if (isLoading || form === null) {
    if (isError) {
      return <ErrorState message={error?.message} requestId={error?.requestId} onRetry={refetch} />;
    }
    return <PageLoader label="Memuat konten..." />;
  }

  const set = (jalur, nilai) => {
    setForm((prev) => {
      const salinan = structuredClone(prev);
      const bagian = jalur.split('.');
      let target = salinan;
      for (const kunci of bagian.slice(0, -1)) target = target[kunci];
      target[bagian.at(-1)] = nilai;
      return salinan;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Konten Landing</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Teks halaman depan. Simpan, lalu buka halaman depan untuk melihat hasilnya.
          </p>
        </div>
        <Button loading={simpan.isPending} onClick={() => simpan.mutate()}>
          Simpan Semua
        </Button>
      </div>

      <Card>
        <CardHeader
          title="Pengumuman"
          description="Banner di paling atas halaman depan, di atas menu. Untuk diskon, fitur baru, atau pemberitahuan singkat."
        />
        <CardBody className="space-y-4">
          <Toggle
            checked={form.announcement.enabled}
            onChange={(v) => set('announcement.enabled', v)}
            label="Tampilkan pengumuman"
            description="Menu halaman depan otomatis bergeser turun mengikuti tinggi banner, berapa pun isinya."
          />

          {form.announcement.enabled && (
            <>
              <PratinjauPengumuman isi={form.announcement} />

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Judul" hint="Bagian tebal di depan. Boleh dikosongkan.">
                  {(p) => (
                    <Input
                      {...p}
                      value={form.announcement.judul}
                      maxLength={120}
                      onChange={(e) => set('announcement.judul', e.target.value)}
                      placeholder="Promo peluncuran"
                    />
                  )}
                </Field>
                <Field label="Warna">
                  {(p) => (
                    <Select
                      {...p}
                      value={form.announcement.nada}
                      onChange={(e) => set('announcement.nada', e.target.value)}
                    >
                      <option value="promo">Promo - kuning menyala</option>
                      <option value="info">Info - biru</option>
                      <option value="peringatan">Peringatan - jingga</option>
                      <option value="gelap">Gelap - menyatu dengan halaman</option>
                    </Select>
                  )}
                </Field>
              </div>

              <Field label="Teks" hint="Satu kalimat. Yang lebih panjang dari itu jarang dibaca sampai habis.">
                {(p) => (
                  <Textarea
                    {...p}
                    rows={2}
                    value={form.announcement.text}
                    maxLength={500}
                    onChange={(e) => set('announcement.text', e.target.value)}
                    placeholder="Semua paket diskon 30 persen sampai akhir bulan."
                  />
                )}
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Alamat gambar"
                  hint="Muncul kecil di sisi kiri. Kosongkan kalau tidak perlu."
                >
                  {(p) => (
                    <Input
                      {...p}
                      value={form.announcement.gambarUrl}
                      maxLength={500}
                      onChange={(e) => set('announcement.gambarUrl', e.target.value)}
                      placeholder="https://... atau /gambar/promo.png"
                    />
                  )}
                </Field>
                <Field label="Lebar isi" hint="Bannernya selalu selebar layar; ini mengatur isinya.">
                  {(p) => (
                    <Select
                      {...p}
                      value={form.announcement.lebar}
                      onChange={(e) => set('announcement.lebar', e.target.value)}
                    >
                      <option value="sempit">Sempit - untuk teks pendek</option>
                      <option value="sedang">Sedang</option>
                      <option value="penuh">Penuh - untuk teks panjang atau bergambar</option>
                    </Select>
                  )}
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Tombol menuju" hint="Boleh jalur dalam situs, mis. /harga">
                  {(p) => (
                    <Input
                      {...p}
                      value={form.announcement.tautanUrl}
                      maxLength={500}
                      onChange={(e) => set('announcement.tautanUrl', e.target.value)}
                      placeholder="/harga"
                    />
                  )}
                </Field>
                <Field label="Tulisan tombol" hint="Tombol muncul hanya kalau keduanya terisi.">
                  {(p) => (
                    <Input
                      {...p}
                      value={form.announcement.tautanLabel}
                      maxLength={60}
                      onChange={(e) => set('announcement.tautanLabel', e.target.value)}
                      placeholder="Lihat harga"
                    />
                  )}
                </Field>
              </div>

              <Toggle
                checked={form.announcement.bisaDitutup}
                onChange={(v) => set('announcement.bisaDitutup', v)}
                label="Pengunjung boleh menutupnya"
                description="Yang menutup tidak melihatnya lagi di peramban itu, sampai isi pengumumannya diganti."
              />
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Hero" description="Bagian pertama yang dibaca pengunjung." />
        <CardBody className="space-y-4">
          <Field label="Badge" hint="Label kecil di atas judul.">
            {(props) => (
              <Input
                {...props}
                value={form.hero.badge}
                maxLength={80}
                onChange={(e) => set('hero.badge', e.target.value)}
              />
            )}
          </Field>
          <Field label="Judul">
            {(props) => (
              <Input
                {...props}
                value={form.hero.title}
                maxLength={160}
                onChange={(e) => set('hero.title', e.target.value)}
              />
            )}
          </Field>
          <Field label="Subjudul">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={form.hero.subtitle}
                maxLength={400}
                onChange={(e) => set('hero.subtitle', e.target.value)}
              />
            )}
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Angka Bukti"
          description="Deretan statistik singkat, maksimal enam."
          action={
            form.stats.length < 6 && (
              <Button
                variant="ghost"
                size="sm"
                icon={Plus}
                onClick={() => set('stats', [...form.stats, { value: '', label: '' }])}
              >
                Tambah
              </Button>
            )
          }
        />
        <CardBody className="space-y-3">
          {form.stats.map((stat, i) => (
            <div key={i} className="flex items-end gap-3">
              <div className="w-32">
                <Field label={i === 0 ? 'Angka' : undefined}>
                  {(props) => (
                    <Input
                      {...props}
                      value={stat.value}
                      maxLength={20}
                      placeholder="3 dtk"
                      onChange={(e) => set(`stats.${i}.value`, e.target.value)}
                    />
                  )}
                </Field>
              </div>
              <div className="flex-1">
                <Field label={i === 0 ? 'Keterangan' : undefined}>
                  {(props) => (
                    <Input
                      {...props}
                      value={stat.label}
                      maxLength={60}
                      placeholder="dari URL ke konfigurasi"
                      onChange={(e) => set(`stats.${i}.label`, e.target.value)}
                    />
                  )}
                </Field>
              </div>
              <Button
                variant="ghost"
                size="sm"
                icon={Trash2}
                iconOnly
                aria-label={`Hapus baris statistik ${i + 1}`}
                onClick={() => set('stats', form.stats.filter((_, idx) => idx !== i))}
              />
            </div>
          ))}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Tanya Jawab"
          description="Bagian FAQ di halaman depan, maksimal dua belas."
          action={
            form.faq.length < 12 && (
              <Button
                variant="ghost"
                size="sm"
                icon={Plus}
                onClick={() => set('faq', [...form.faq, { question: '', answer: '' }])}
              >
                Tambah
              </Button>
            )
          }
        />
        <CardBody className="space-y-4">
          {form.faq.length === 0 && (
            <p className="text-sm text-[var(--text-muted)]">
              Belum ada FAQ dari admin; halaman depan memakai daftar bawaan.
            </p>
          )}
          {form.faq.map((item, i) => (
            <div key={i} className="rounded-lg border border-[var(--border-subtle)] p-3">
              <div className="mb-2 flex items-start gap-2">
                <div className="flex-1">
                  <Field label="Pertanyaan">
                    {(props) => (
                      <Input
                        {...props}
                        value={item.question}
                        maxLength={200}
                        onChange={(e) => set(`faq.${i}.question`, e.target.value)}
                      />
                    )}
                  </Field>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Trash2}
                  iconOnly
                  aria-label={`Hapus FAQ ${i + 1}`}
                  className="mt-6"
                  onClick={() => set('faq', form.faq.filter((_, idx) => idx !== i))}
                />
              </div>
              <Field label="Jawaban">
                {(props) => (
                  <Textarea
                    {...props}
                    rows={2}
                    value={item.answer}
                    maxLength={1000}
                    onChange={(e) => set(`faq.${i}.answer`, e.target.value)}
                  />
                )}
              </Field>
            </div>
          ))}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Tentang Kami"
          description="Bagian yang menjawab 'ini siapa' sebelum pengunjung bertanya 'ini bisa apa'. Tidak ditampilkan kalau dikosongkan."
        />
        <CardBody className="space-y-4">
          <Toggle
            checked={form.tentang.aktif}
            onChange={(v) => set('tentang.aktif', v)}
            label="Tampilkan bagian Tentang Kami"
            description="Muncul di halaman depan, sesudah bagian FAQ."
          />

          {form.tentang.aktif && (
            <>
              <Field label="Judul" hint="Kosong berarti memakai 'Siapa di balik Crawel'.">
                {(p) => (
                  <Input
                    {...p}
                    value={form.tentang.judul}
                    maxLength={120}
                    onChange={(e) => set('tentang.judul', e.target.value)}
                    placeholder="Siapa di balik Crawel"
                  />
                )}
              </Field>

              <Field
                label="Cerita"
                hint="Pisahkan paragraf dengan satu baris kosong. Tulis untuk orang yang belum kenal Crawel sama sekali."
              >
                {(p) => (
                  <Textarea
                    {...p}
                    rows={6}
                    value={form.tentang.isi}
                    maxLength={2000}
                    onChange={(e) => set('tentang.isi', e.target.value)}
                    placeholder={
                      'Crawel lahir dari satu pekerjaan yang berulang setiap minggu.\n\n' +
                      'Sekarang dipakai untuk memantau harga, mengumpulkan lowongan, dan menyusun data yang tersebar di banyak halaman.'
                    }
                  />
                )}
              </Field>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-semibold text-[var(--text-primary)]">
                    Poin yang ingin ditonjolkan
                  </p>
                  <Button
                    size="xs"
                    variant="ghost"
                    icon={Plus}
                    onClick={() =>
                      set('tentang.poin', [...(form.tentang.poin ?? []), { judul: '', isi: '' }])
                    }
                  >
                    Tambah
                  </Button>
                </div>

                {(form.tentang.poin ?? []).length === 0 ? (
                  <p className="text-xs text-[var(--text-muted)]">
                    Belum ada. Contoh yang berguna: berdiri sejak kapan, berapa situs yang sudah
                    ditangani, atau prinsip yang Anda pegang.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {(form.tentang.poin ?? []).map((poin, i) => (
                      <div
                        key={i}
                        className="flex gap-2 rounded-lg border border-[var(--border-subtle)] p-3"
                      >
                        <div className="grid flex-1 gap-2 sm:grid-cols-[1fr_2fr]">
                          <Input
                            value={poin.judul}
                            maxLength={80}
                            placeholder="Berdiri 2026"
                            onChange={(e) => {
                              const baru = [...form.tentang.poin];
                              baru[i] = { ...baru[i], judul: e.target.value };
                              set('tentang.poin', baru);
                            }}
                          />
                          <Input
                            value={poin.isi}
                            maxLength={300}
                            placeholder="Keterangan singkat"
                            onChange={(e) => {
                              const baru = [...form.tentang.poin];
                              baru[i] = { ...baru[i], isi: e.target.value };
                              set('tentang.poin', baru);
                            }}
                          />
                        </div>
                        <Button
                          size="xs"
                          variant="ghost"
                          icon={Trash2}
                          iconOnly
                          aria-label="Hapus poin"
                          onClick={() =>
                            set(
                              'tentang.poin',
                              form.tentang.poin.filter((_, j) => j !== i),
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Hubungi Kami"
          description="Dipakai di bagian Hubungi Kami sekaligus di footer semua halaman publik. Yang dikosongkan tidak ditampilkan."
        />
        <CardBody className="space-y-4">
          <KelengkapanKontak kontak={form.kontak} />

          <Toggle
            checked={form.kontak.aktif}
            onChange={(v) => set('kontak.aktif', v)}
            label="Tampilkan bagian Hubungi Kami"
            description="Footer tetap memakai data di bawah ini walau bagian ini dimatikan."
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nama usaha" hint="Tercantum di baris hak cipta pada footer.">
              {(p) => (
                <Input
                  {...p}
                  value={form.kontak.namaUsaha}
                  maxLength={120}
                  onChange={(e) => set('kontak.namaUsaha', e.target.value)}
                  placeholder="Crawel"
                />
              )}
            </Field>
            <Field label="Judul bagian" hint="Kosong berarti 'Ada yang ingin ditanyakan?'">
              {(p) => (
                <Input
                  {...p}
                  value={form.kontak.judul}
                  maxLength={120}
                  onChange={(e) => set('kontak.judul', e.target.value)}
                  placeholder="Ada yang ingin ditanyakan?"
                />
              )}
            </Field>
          </div>

          <Field label="Pengantar" hint="Satu atau dua kalimat di atas kartu kontak.">
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                value={form.kontak.pengantar}
                maxLength={500}
                onChange={(e) => set('kontak.pengantar', e.target.value)}
                placeholder="Kami membalas pada hari kerja, biasanya dalam satu hari."
              />
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Email support">
              {(p) => (
                <Input
                  {...p}
                  value={form.kontak.email}
                  maxLength={160}
                  onChange={(e) => set('kontak.email', e.target.value)}
                  placeholder="support@crawel.com"
                />
              )}
            </Field>
            <Field
              label="Nomor telepon"
              hint="Diminta sebagian penyedia pembayaran sebagai kontak support, dan WhatsApp tidak selalu dihitung menggantikannya."
            >
              {(p) => (
                <Input
                  {...p}
                  value={form.kontak.telepon}
                  maxLength={40}
                  onChange={(e) => set('kontak.telepon', e.target.value)}
                  placeholder="+62 21 1234 5678"
                />
              )}
            </Field>
            <Field label="WhatsApp" hint="Boleh ditulis dengan spasi; tautannya dirapikan sendiri.">
              {(p) => (
                <Input
                  {...p}
                  value={form.kontak.whatsapp}
                  maxLength={40}
                  onChange={(e) => set('kontak.whatsapp', e.target.value)}
                  placeholder="+62 812 3456 7890"
                />
              )}
            </Field>
            <Field label="Jam operasional">
              {(p) => (
                <Input
                  {...p}
                  value={form.kontak.jamOperasional}
                  maxLength={160}
                  onChange={(e) => set('kontak.jamOperasional', e.target.value)}
                  placeholder="Senin sampai Jumat, 09.00 sampai 17.00 WIB"
                />
              )}
            </Field>
          </div>

          <Field
            label="Alamat usaha"
            hint="Alamat yang bisa dituju membuat pengunjung percaya ada usaha nyata di baliknya, dan sebagian penyedia pembayaran menuntutnya tercantum."
          >
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                value={form.kontak.alamat}
                maxLength={400}
                onChange={(e) => set('kontak.alamat', e.target.value)}
                placeholder="Jalan Contoh No. 12, Jakarta Selatan 12345"
              />
            )}
          </Field>

          <Field
            label="Email kontak lama"
            hint="Dipakai kalau Email support di atas dikosongkan. Boleh dibiarkan."
          >
            {(p) => (
              <Input
                {...p}
                type="email"
                value={form.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
                placeholder="halo@crawel.com"
              />
            )}
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Media Sosial"
          description="Tampil di footer. Isi alamat lengkapnya, dan yang dikosongkan tidak ditampilkan."
        />
        <CardBody>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              ['instagram', 'Instagram', 'https://instagram.com/crawel'],
              ['linkedin', 'LinkedIn', 'https://linkedin.com/company/crawel'],
              ['twitter', 'X', 'https://x.com/crawel'],
              ['youtube', 'YouTube', 'https://youtube.com/@crawel'],
              ['github', 'GitHub', 'https://github.com/crawel'],
            ].map(([kunci, label, contoh]) => (
              <Field key={kunci} label={label}>
                {(p) => (
                  <Input
                    {...p}
                    value={form.sosial[kunci]}
                    maxLength={200}
                    onChange={(e) => set(`sosial.${kunci}`, e.target.value)}
                    placeholder={contoh}
                  />
                )}
              </Field>
            ))}
          </div>
        </CardBody>
      </Card>

      <div className="flex justify-end">
        <Button loading={simpan.isPending} onClick={() => simpan.mutate()}>
          Simpan Semua
        </Button>
      </div>
    </div>
  );
};
